import React from 'react';
import { User } from '../../types/User';
import { TableColumn } from '../../utils/tableUtils';

// Helper function to get status color
const getStatusColor = (status: string, type: 'status' | 'approval' | 'role') => {
  if (type === 'status') {
    return status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  }
  if (type === 'approval') {
    if (status === 'Approved') return 'bg-green-100 text-green-800';
    if (status === 'Pending') return 'bg-yellow-100 text-yellow-800';
    if (status === 'Rejected') return 'bg-red-100 text-red-800';
  }
  if (type === 'role') {
    if (status === 'Admin') return 'bg-purple-100 text-purple-800';
    if (status === 'Teacher') return 'bg-blue-100 text-blue-800';
    if (status === 'Parent') return 'bg-orange-100 text-orange-800';
    if (status === 'Student') return 'bg-gray-100 text-gray-800';
  }
  return 'bg-gray-100 text-gray-800';
};

// Status badge component
const StatusBadge: React.FC<{ status: string; type: 'status' | 'approval' | 'role' }> = ({ status, type }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status, type)}`}>
    {status}
  </span>
);

// Define user table columns
export const userColumns: TableColumn<User>[] = [
  {
    id: 'name',
    label: 'Name',
    accessor: (user) => `${user.firstName} ${user.lastName}`,
    sortable: true,
  },
  {
    id: 'username',
    label: 'Username',
    accessor: (user) => user.username,
    sortable: true,
  },
  {
    id: 'email',
    label: 'Email',
    accessor: (user) => user.email,
    sortable: true,
  },
  {
    id: 'role',
    label: 'Role',
    accessor: (user) => <StatusBadge status={user.role} type="role" />,
    sortable: true,
  },
  {
    id: 'status',
    label: 'Status',
    accessor: (user) => <StatusBadge status={user.active ? 'Active' : 'Inactive'} type="status" />,
    sortable: true,
  },
  {
    id: 'approvalStatus',
    label: 'Approval',
    accessor: (user) => <StatusBadge status={user.approvalStatus} type="approval" />,
    sortable: true,
  },
  {
    id: 'createdAt',
    label: 'Created',
    accessor: (user) => user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A',
    sortable: true,
  },
  {
    id: 'actions',
    label: 'Actions',
    accessor: () => null, // Actions are handled by the AdminTable component
    sortable: false,
  },
];

// User table configuration for AdminTable
export const userTableConfig = {
  entityName: 'User',
  apiEndpoint: '/api/admin/users',
  columns: userColumns,
  searchFields: ['firstName', 'lastName', 'username', 'email'] as (keyof User)[],
  exportFileName: 'users',
  defaultVisibleColumns: {
    name: true,
    username: true,
    email: true,
    role: true,
    status: true,
    approvalStatus: true,
    createdAt: true,
    actions: true,
  },
  filterOptions: {
    role: [
      { value: 'Student', label: 'Student' },
      { value: 'Teacher', label: 'Teacher' },
      { value: 'Parent', label: 'Parent' },
      { value: 'Admin', label: 'Admin' },
    ],
    status: [
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
    ],
    approvalStatus: [
      { value: 'Approved', label: 'Approved' },
      { value: 'Pending', label: 'Pending' },
      { value: 'Rejected', label: 'Rejected' },
    ],
  },
  actions: {
    showView: true,
    showEdit: true,
    showDelete: true,
    customActions: [],
  },
};
